import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, vModelSelect as _vModelSelect, withDirectives as _withDirectives, createCommentVNode as _createCommentVNode, createVNode as _createVNode, createTextVNode as _createTextVNode, resolveDirective as _resolveDirective } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "modal-mask"
}
const _hoisted_2 = { class: "modal-wrapper" }
const _hoisted_3 = { class: "modal-dialog" }
const _hoisted_4 = { class: "modal-content" }
const _hoisted_5 = { class: "modal-body" }
const _hoisted_6 = { class: "form-group" }
const _hoisted_7 = ["value"]
const _hoisted_8 = {
  key: 0,
  class: "form-group"
}
const _hoisted_9 = ["value"]
const _hoisted_10 = { class: "modal-footer" }
const _hoisted_11 = ["disabled"]
const _hoisted_12 = {
  key: 1,
  class: "container",
  style: {"margin-top":"80px","padding-top":"40px"}
}
const _hoisted_13 = { class: "mt-3" }
const _hoisted_14 = { class: "table table-bordered table-hover" }
const _hoisted_15 = { class: "text-muted" }
const _hoisted_16 = { class: "dropdown" }
const _hoisted_17 = { class: "dropdown-menu dropdown-menu-right" }
const _hoisted_18 = { class: "dropdown-item" }
const _hoisted_19 = ["onClick"]
const _hoisted_20 = ["onClick"]

import { computed, onMounted, watch, shallowReactive, type Ref } from 'vue'
    import { useRoute } from 'vue-router'

    import { Refresh } from '@browser/Browser'
    import { generateName } from '@common/Common'
    import { guid } from '@common/Guid'

    import CorporateNavigation from '@common/Components/CorporateNavigation.vue'
    import PortfolioCreatorDialog from '@Quest/Portfolios/Components/PortfolioCreatorDialog.vue'

    import
        {
            type CreatePortfolioEventPayload,
            PortfolioCreatorDialogViewModel
        } from '@Quest/Portfolios/Components/PortfolioCreatorDialog'

    import
        {
            usePortfoliosPageQueryV1,
            useLinkeableTchProjectsByWorkspaceIdQueryV1
        } from '@Quest/Portfolios/queries-vue-composables'

    import
        {
            useCreatePortfolioCommandV1,
            useDeletePortfolioCommandV1,
            useLinkPortfolioCommandV1
        } from '@Quest/Portfolios/commands-vue-composables'

    import type {
        PortfolioReadModel,
        PortfoliosPageReadModel,
        TchProjectReadModel,
        WorkspaceTchProjectsListReadModel
    } from '@Quest/Portfolios/readmodels'


    // ======================
    // Route
    // ======================

    
export default /*@__PURE__*/_defineComponent({
  __name: 'PortfoliosPage',
  setup(__props) {

    const route = useRoute()

    const workspaceId = computed(() => Number(route.params?.workspaceId ?? 0))
    const strategyId = computed(() => String(route.params?.strategyId ?? ''))


    // ======================
    // Queries
    // ======================

    const {
        runPortfoliosPageQueryV1,
        modelPortfoliosPageQueryV1
    } = usePortfoliosPageQueryV1(workspaceId, strategyId)

    const rm = modelPortfoliosPageQueryV1 as Ref<PortfoliosPageReadModel | null>


    const {
        runLinkeableTchProjectsByWorkspaceIdQueryV1,
        modelLinkeableTchProjectsByWorkspaceIdQueryV1
    } = useLinkeableTchProjectsByWorkspaceIdQueryV1()

    const tchProjectsModel =
        modelLinkeableTchProjectsByWorkspaceIdQueryV1 as Ref<WorkspaceTchProjectsListReadModel | null>

    const tchProjects = computed<TchProjectReadModel[]>(() =>
        tchProjectsModel.value?.Projects ?? []
    )


    // ======================
    // Commands
    // ======================

    const {
        invokeCreatePortfolioCommandV1,
        onSucceedCreatePortfolioCommandV1
    } = useCreatePortfolioCommandV1(workspaceId)

    const {
        invokeDeletePortfolioCommandV1,
        onSucceedDeletePortfolioCommandV1
    } = useDeletePortfolioCommandV1(workspaceId)

    const {
        invokeLinkPortfolioCommandV1,
        onSucceedLinkPortfolioCommandV1
    } = useLinkPortfolioCommandV1(workspaceId, strategyId)


    // ======================
    // ViewModels
    // ======================

    class PortfolioLinkerViewModel
    {
        opened: boolean = false
        portfolioId: string | null = null
        workspaceId: number | null = null
        tchProjectId: number | null = null

        get canSave()
        {
            return this.workspaceId && this.tchProjectId
        }

        close()
        {
            this.opened = false
        }
    }

    const portfolioLinker = shallowReactive(new PortfolioLinkerViewModel())
    const portfolioCreatorDialog = shallowReactive(new PortfolioCreatorDialogViewModel())


    // ======================
    // Computed
    // ======================

    const validPortfolios = computed(() =>
        rm.value?.Portfolios.filter(a => a.IsTchProjectDeleted === false) ?? []
    )

    const invalidPortfolios = computed(() =>
        rm.value?.Portfolios.filter(a => a.IsTchProjectDeleted === true) ?? []
    )


    // ======================
    // Watchers
    // ======================

    watch(
        () => portfolioLinker.workspaceId,
        (val) =>
        {
            if (!val) return
            runLinkeableTchProjectsByWorkspaceIdQueryV1({ WorkspaceId: val })
        }
    )

    onSucceedCreatePortfolioCommandV1(() =>
    {
        portfolioCreatorDialog.close()
        Refresh()
    })

    onSucceedLinkPortfolioCommandV1(() =>
    {
        portfolioLinker.close()
        Refresh()
    })

    onSucceedDeletePortfolioCommandV1(() =>
    {
        Refresh()
    })

    // ======================
    // Methods
    // ======================

    function showPortfolioLinker()
    {
        portfolioLinker.portfolioId = guid()
        portfolioLinker.opened = true
    }

    function closePortfolioLinker()
    {
        portfolioLinker.opened = false
    }

    function showPortfolioCreator()
    {
        if (!rm.value) return
        portfolioCreatorDialog.show(
            workspaceId.value,
            generateName("Portfolio"),
            rm.value.Workspaces
        )
    }

    function handleDeletePortfolioCommand(p: PortfolioReadModel)
    {
        if (confirm("Are you sure?"))
        {
            invokeDeletePortfolioCommandV1({ Id: p.Id })
        }
    }

    function handleCreatePortfolioCommand(payload: CreatePortfolioEventPayload)
    {
        invokeCreatePortfolioCommandV1({
            PortfolioWorkspaceId: payload.portfolioWorkspaceId,
            StrategyId: strategyId.value,
            Id: guid(),
            Name: payload.portfolioName,
            Description: payload.portfolioDescription
        })
    }

    function handleLinkPortfolioCommand()
    {
        if (!portfolioLinker.portfolioId ||
            !portfolioLinker.workspaceId ||
            !portfolioLinker.tchProjectId) return

        invokeLinkPortfolioCommandV1({
            Id: portfolioLinker.portfolioId,
            PortfolioWorkspaceId: portfolioLinker.workspaceId,
            PortfolioTchProjectId: portfolioLinker.tchProjectId
        })
    }


    // ======================
    // Lifecycle
    // ======================

    onMounted(() =>
    {
        document.title = "Portfolios | TransparentChoice"
        runPortfoliosPageQueryV1()
    })

return (_ctx: any,_cache: any) => {
  const _directive_sanitizedHref = _resolveDirective("sanitizedHref")!

  return (_openBlock(), _createElementBlock("div", null, [
    (_unref(portfolioLinker).opened)
      ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
          _createElementVNode("div", _hoisted_2, [
            _createElementVNode("div", _hoisted_3, [
              _createElementVNode("div", _hoisted_4, [
                _createElementVNode("div", { class: "modal-header" }, [
                  _cache[2] || (_cache[2] = _createElementVNode("h5", { class: "modal-title" }, "Link portfolio", -1 /* CACHED */)),
                  _createElementVNode("button", {
                    onClick: closePortfolioLinker,
                    type: "button",
                    class: "close"
                  }, "×")
                ]),
                _createElementVNode("div", _hoisted_5, [
                  _createElementVNode("div", _hoisted_6, [
                    _cache[3] || (_cache[3] = _createElementVNode("label", null, "Workspace:", -1 /* CACHED */)),
                    _withDirectives(_createElementVNode("select", {
                      "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((_unref(portfolioLinker).workspaceId) = $event)),
                      class: "form-control"
                    }, [
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(rm).Workspaces, (w) => {
                        return (_openBlock(), _createElementBlock("option", {
                          value: w.WorkspaceId
                        }, _toDisplayString(w.Name), 9 /* TEXT, PROPS */, _hoisted_7))
                      }), 256 /* UNKEYED_FRAGMENT */))
                    ], 512 /* NEED_PATCH */), [
                      [_vModelSelect, _unref(portfolioLinker).workspaceId]
                    ])
                  ]),
                  (tchProjects.value != null)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
                        _cache[4] || (_cache[4] = _createElementVNode("label", null, "Portfolio:", -1 /* CACHED */)),
                        _withDirectives(_createElementVNode("select", {
                          "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((_unref(portfolioLinker).tchProjectId) = $event)),
                          class: "form-control"
                        }, [
                          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(tchProjects.value, (p) => {
                            return (_openBlock(), _createElementBlock("option", {
                              value: p.Id
                            }, _toDisplayString(p.Name), 9 /* TEXT, PROPS */, _hoisted_9))
                          }), 256 /* UNKEYED_FRAGMENT */))
                        ], 512 /* NEED_PATCH */), [
                          [_vModelSelect, _unref(portfolioLinker).tchProjectId]
                        ])
                      ]))
                    : _createCommentVNode("v-if", true)
                ]),
                _createElementVNode("div", _hoisted_10, [
                  _createElementVNode("button", {
                    onClick: handleLinkPortfolioCommand,
                    type: "button",
                    class: "btn btn-primary",
                    disabled: !_unref(portfolioLinker).canSave
                  }, "Link", 8 /* PROPS */, _hoisted_11),
                  _createElementVNode("button", {
                    onClick: closePortfolioLinker,
                    type: "button",
                    class: "btn btn-outline-secondary"
                  }, "Cancel")
                ])
              ])
            ])
          ])
        ]))
      : _createCommentVNode("v-if", true),
    _createVNode(PortfolioCreatorDialog, {
      viewModel: _unref(portfolioCreatorDialog),
      onCreatePortfolio: handleCreatePortfolioCommand
    }, null, 8 /* PROPS */, ["viewModel"]),
    _createVNode(CorporateNavigation, { ActivePage: "portfolios" }),
    (_unref(rm))
      ? (_openBlock(), _createElementBlock("div", _hoisted_12, [
          _createElementVNode("div", { class: "row" }, [
            _cache[6] || (_cache[6] = _createElementVNode("div", { class: "col-lg-6" }, [
              _createElementVNode("h1", { style: {"font-size":"2em","font-weight":"300","color":"#33475b","margin":"0"} }, "Portfolios")
            ], -1 /* CACHED */)),
            _createElementVNode("div", { class: "col-lg-6 text-right align-self-end" }, [
              _createElementVNode("div", { class: "btn-group" }, [
                _createElementVNode("button", {
                  role: "button",
                  class: "btn btn-success",
                  style: {"padding":"0.7em 2em"},
                  onClick: showPortfolioCreator
                }, "Create portfolio"),
                _cache[5] || (_cache[5] = _createElementVNode("button", {
                  type: "button",
                  class: "btn btn-outline-success dropdown-toggle dropdown-toggle-split",
                  "data-toggle": "dropdown"
                }, [
                  _createElementVNode("span", { class: "sr-only" }, "Toggle Dropdown")
                ], -1 /* CACHED */)),
                _createElementVNode("div", { class: "dropdown-menu dropdown-menu-right" }, [
                  _createElementVNode("a", {
                    class: "dropdown-item",
                    onClick: showPortfolioLinker
                  }, "Link existing portfolio")
                ])
              ])
            ])
          ]),
          _cache[12] || (_cache[12] = _createElementVNode("hr", { style: {"border-color":"#bbb","margin":"20px 0 60px"} }, null, -1 /* CACHED */)),
          _createElementVNode("div", _hoisted_13, [
            _createElementVNode("table", _hoisted_14, [
              _cache[11] || (_cache[11] = _createElementVNode("thead", null, [
                _createElementVNode("tr", null, [
                  _createElementVNode("th", null, "Portfolio"),
                  _createElementVNode("th", { style: {"width":"120px"} })
                ])
              ], -1 /* CACHED */)),
              _createElementVNode("tbody", null, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(validPortfolios.value, (p) => {
                  return (_openBlock(), _createElementBlock("tr", {
                    key: p.Id
                  }, [
                    _createElementVNode("td", null, [
                      _withDirectives((_openBlock(), _createElementBlock("a", null, [
                        _createTextVNode(_toDisplayString(p.Name), 1 /* TEXT */)
                      ])), [
                        [_directive_sanitizedHref, `/#/portfolio/kanban/${p.WorkspaceId}/${p.TchProjectId}`]
                      ]),
                      _cache[7] || (_cache[7] = _createElementVNode("br", null, null, -1 /* CACHED */)),
                      _createElementVNode("small", _hoisted_15, "Workspace: " + _toDisplayString(p.WorkspaceName), 1 /* TEXT */)
                    ]),
                    _createElementVNode("td", null, [
                      _createElementVNode("div", _hoisted_16, [
                        _cache[10] || (_cache[10] = _createElementVNode("button", {
                          type: "button",
                          class: "btn btn-outline-secondary dropdown-toggle",
                          "data-toggle": "dropdown"
                        }, " Actions ", -1 /* CACHED */)),
                        _createElementVNode("div", _hoisted_17, [
                          _withDirectives((_openBlock(), _createElementBlock("a", _hoisted_18, [...(_cache[8] || (_cache[8] = [
                            _createTextVNode("Settings", -1 /* CACHED */)
                          ]))])), [
                            [_directive_sanitizedHref, `/#/portfolio/settings/${workspaceId.value}/${p.TchProjectId}`]
                          ]),
                          _cache[9] || (_cache[9] = _createElementVNode("div", { class: "dropdown-divider" }, null, -1 /* CACHED */)),
                          _createElementVNode("a", {
                            class: "dropdown-item",
                            onClick: ($event: any) => (handleDeletePortfolioCommand(p))
                          }, "Delete", 8 /* PROPS */, _hoisted_19)
                        ])
                      ])
                    ])
                  ]))
                }), 128 /* KEYED_FRAGMENT */)),
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(invalidPortfolios.value, (p) => {
                  return (_openBlock(), _createElementBlock("tr", {
                    key: p.Id,
                    class: "table-danger"
                  }, [
                    _createElementVNode("td", null, _toDisplayString(p.Name), 1 /* TEXT */),
                    _createElementVNode("td", null, [
                      _createElementVNode("button", {
                        type: "button",
                        class: "btn btn-outline-secondary",
                        onClick: ($event: any) => (handleDeletePortfolioCommand(p))
                      }, " Delete ", 8 /* PROPS */, _hoisted_20)
                    ])
                  ]))
                }), 128 /* KEYED_FRAGMENT */))
              ])
            ])
          ])
        ]))
      : _createCommentVNode("v-if", true)
  ]))
}
}

})